import type { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { lazyCopyMailListRows, lazyMoveMailListRows } from 'owa-mail-triage-action';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isPublicFolderSelected } from 'owa-publicfolder-utils';
import { getGroupFolderAndRulePermission } from 'owa-groups-shared-store/lib/selectors/groupFolderRulePermissionStoreSelectors';
import { getIndexerValueForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import { lazyDropMailListRowsOnGroup } from 'owa-group-mail-left-nav-actions';
import {
    getStore as getListViewStore,
    type TableQueryType,
    getSelectedTableView,
    isMailListConversationView,
} from 'owa-mail-list-store';
import { isSearchFolderForFolderId } from 'owa-favorites';
import { logUsage } from 'owa-analytics';
import ClipboardErrorRegular from 'owa-fluent-icons-svg/lib/icons/ClipboardErrorRegular';
import { getListViewNotificationDimensions } from 'owa-mail-layout';
import { lazyShowNotification } from 'owa-notification-bar';
import loc from 'owa-localize';
import {
    onCopyFailedNotificationText,
    onMoveFailedNotificationText,
} from './onPaste.locstring.json';
import { OWA_ITEM_CLIPBAORD_MIME } from 'owa-clipboard-utils';
import { isMoveCopyAllowed } from 'owa-mail-triage-movecopy-importexport/lib/utils/copyMoveItemUtils';
import { lazyReloadTable } from 'owa-mail-triage-table-load-extra';
import type { TableView } from 'owa-mail-list-store';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

export default async function onPaste(event: KeyboardEvent): Promise<void> {
    if (event.ctrlKey && event.key === 'v' && !!window.navigator.clipboard.read) {
        event.preventDefault();
        const clipboardItems = await window.navigator.clipboard.read();
        const destinationTableView = getSelectedTableView();

        // Destination MailboxInfo will be used for logUsage as onPaste action is done in the destination
        const destinationMailboxInfo = getMailboxInfo(destinationTableView);

        // These values can be obtained from the first clipboard item only
        let isCopy: boolean | undefined = undefined;
        let originalTableView: TableView | undefined = undefined;
        let listViewType: ReactListViewType | undefined = undefined;

        for (const clipboardItem of clipboardItems) {
            for (const type of clipboardItem.types) {
                if (!(type === OWA_ITEM_CLIPBAORD_MIME)) {
                    return;
                }
                const blob = await clipboardItem.getType(type);
                const dragInfo: MailListRowDragData = JSON.parse(await blob.text());
                const dragItemType = dragInfo.itemType;

                // Copy/Move item part is not supported
                if (
                    dragItemType !== DraggableItemTypes.MailListRow &&
                    dragItemType !== DraggableItemTypes.MultiMailListConversationRows &&
                    dragItemType !== DraggableItemTypes.MultiMailListMessageRows
                ) {
                    return;
                }

                if (!originalTableView) {
                    isCopy = dragInfo.isCopy;
                    originalTableView = getListViewStore().tableViews.get(dragInfo.tableViewId);
                    listViewType = originalTableView?.tableQuery.listViewType;
                }

                let sourceMailboxInfo: MailboxInfo = getMailboxInfo(originalTableView);

                // Get source mailbox info from dragInfo if available
                if (dragInfo.itemData) {
                    const draggedItemData = dragInfo.itemData as {
                        mailboxInfo: MailboxInfo;
                    };
                    sourceMailboxInfo = draggedItemData?.mailboxInfo ?? sourceMailboxInfo;
                }

                if (!originalTableView || !destinationTableView) {
                    const logInfo = {
                        fromSearch: dragInfo.tableViewId.includes('query'),
                        isOriginalTableViewDefined: !!originalTableView,
                        isDestinationTableViewDefined: !!destinationTableView,
                        isCopy,
                    };
                    const notificationDimensions = getListViewNotificationDimensions();

                    lazyShowNotification.importAndExecute(
                        'pasteErrorNotification',
                        'MailModuleNotificationBarHost',
                        window,
                        loc(isCopy ? onCopyFailedNotificationText : onMoveFailedNotificationText),
                        {
                            icon: ClipboardErrorRegular,
                            minWidth: notificationDimensions.minWidth,
                            maxWidth: notificationDimensions.maxWidth,
                        },
                        undefined /* renderContent */,
                        true /* useV9 */
                    );
                    // Logged once only when paste fails
                    logOnPasteFailed(destinationMailboxInfo, logInfo);
                    return;
                }

                // Paste in Folder
                if (destinationTableView.tableQuery.type === 0) {
                    const destinationFolderId = destinationTableView.tableQuery.folderId;

                    // Checks based on source and destination mailboxes to determine if the move/copy is allowed
                    // Conversations can't be copied to Public Folders
                    // Copy/Move not allowed in Search Folders
                    if (
                        !isMoveCopyAllowed(sourceMailboxInfo, destinationMailboxInfo) ||
                        (isMailListConversationView(originalTableView) &&
                            isPublicFolderSelected()) ||
                        isSearchFolderForFolderId(destinationFolderId)
                    ) {
                        return;
                    }

                    // Archive mailboxes and shared mailboxes don't support notifications
                    // so we will reload the table after the copy/move so the view is updated
                    // and the user can see the changes
                    const shouldReloadTable =
                        destinationMailboxInfo.type === 'ArchiveMailbox' ||
                        destinationMailboxInfo.type === 'SharedMailbox';
                    if (isCopy) {
                        lazyCopyMailListRows
                            .importAndExecute(
                                dragInfo.rowKeys,
                                originalTableView,
                                destinationFolderId,
                                'Keyboard'
                            )
                            .then(_result => {
                                if (shouldReloadTable) {
                                    lazyReloadTable.importAndExecute(destinationTableView);
                                }
                            });
                    } else {
                        lazyMoveMailListRows
                            .importAndExecute(
                                dragInfo.rowKeys,
                                destinationFolderId,
                                dragInfo.tableViewId,
                                'Keyboard'
                            )
                            .then(_result => {
                                if (shouldReloadTable) {
                                    lazyReloadTable.importAndExecute(destinationTableView);
                                }
                            });
                    }
                    // Paste in Group
                } else if (
                    (destinationTableView.tableQuery.type === 2 ||
                        destinationTableView.tableQuery.type === 3) &&
                    (dragItemType === DraggableItemTypes.MultiMailListMessageRows ||
                        dragItemType === DraggableItemTypes.MailListRow)
                ) {
                    const folderRulePermission = getGroupFolderAndRulePermission(
                        destinationMailboxInfo,
                        destinationTableView.tableQuery.folderId // folderId == groupId
                    );

                    // Copy/Move not allowed if user doesn't have copy/move permission, across Group mailboxes, or across accounts
                    if (
                        (isCopy && !folderRulePermission?.canCopyMessage) ||
                        (!isCopy && folderRulePermission?.canMoveMessage) ||
                        (sourceMailboxInfo.type === 'GroupMailbox' &&
                            sourceMailboxInfo.mailboxSmtpAddress !==
                                destinationTableView.tableQuery.folderId) ||
                        getIndexerValueForMailboxInfo(destinationMailboxInfo) !==
                            getIndexerValueForMailboxInfo(sourceMailboxInfo)
                    ) {
                        return;
                    }

                    lazyDropMailListRowsOnGroup.importAndExecute(
                        dragInfo,
                        destinationTableView.tableQuery.folderId /* groupId */,
                        isCopy /* copy */,
                        'Keyboard'
                    );
                }
            }
        }
        // Log only once per paste event even if multiple clipboard items are processed
        // as they all have the same isCopy and listViewType values
        logUsage('TnS_PasteWithShortcut', destinationMailboxInfo, {
            listViewType,
            isCopy,
        });
    }
}

function logOnPasteFailed(mailboxInfo: MailboxInfo, logInfo: any) {
    logUsage('TnS_OnPasteFailed', mailboxInfo, logInfo);
}
